import { NavLink, Outlet, useParams } from "react-router-dom";
import { Box, Skeleton, styled } from "@mui/material";
import CustomBackButton from "@/components/custom/custom-back-button";
import { formatLabel } from "@/utils/format-label";
import { useChannelStats } from "@/hooks/use-channel-stats";

const StatusPill = styled(Box)(({ status }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  borderRadius: 999,
  fontSize: "0.7rem",
  fontWeight: 500,
  padding: "3px 10px",
  backgroundColor: status === "active" ? "#f0fdf4" : "#fef2f2",
  color: status === "active" ? "#15803d" : "#dc2626",
}));

const StatusDot = styled("span")(({ status }) => ({
  width: 6,
  height: 6,
  borderRadius: "50%",
  flexShrink: 0,
  backgroundColor: status === "active" ? "#15803d" : "#dc2626",
}));

const TabsWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor: theme.palette.divider,
  borderRadius: 999,
  padding: 4,
  width: "fit-content",
  gap: 2,
  marginBottom: theme.spacing(3),
}));

const TabLink = styled(NavLink)(({ theme }) => ({
  padding: "5px 20px",
  borderRadius: 999,
  fontSize: "0.875rem",
  fontWeight: 500,
  textDecoration: "none",
  color: theme.palette.text.secondary,
  transition: "all 150ms ease",
  whiteSpace: "nowrap",

  "&:hover": {
    color: theme.palette.text.primary,
  },

  "&.active": {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
}));

const tabs = [
  { name: "Overview", path: "overview" },
  // { name: "Configuration", path: "configuration" },
  { name: "Transactions", path: "transactions" },
];

const SingleProductOutlet = () => {
  const { productId } = useParams();
  const { data, isLoading } = useChannelStats("30d");
  const channelStats = data?.channels?.find((c) => c.channel === productId);
  const status = channelStats && (channelStats.totalCount > 0 ? "active" : "inactive");

  return (
    <Box sx={{ m: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
        <CustomBackButton path="/products" />
        <Box component="span" sx={{ fontSize: "0.95rem", fontWeight: 600 }}>
          {formatLabel(productId)}
        </Box>
        {isLoading ? (
          <Skeleton variant="rounded" width={64} height={20} sx={{ borderRadius: 999 }} />
        ) : (
          status && (
            <StatusPill status={status}>
              <StatusDot status={status} />
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </StatusPill>
          )
        )}
      </Box>

      <TabsWrapper>
        {tabs.map((tab) => (
          <TabLink
            key={tab.path}
            to={`/products/${tab.path}/${productId}`}
          >
            {tab.name}
          </TabLink>
        ))}
      </TabsWrapper>

      <Outlet />
    </Box>
  );
};

export default SingleProductOutlet;